import * as config from 'serviceWorker/db/config';
import { ConfigData } from 'serviceWorker/db/config';
import { pull, type PullOptions } from '../pull';
import { defaultConfig } from 'serviceWorker/helpers/httpRequests';
import { buildWorkerRequestHeaders } from 'serviceWorker/helpers/requestHeaders';
import { apiUrl as apiUrlFromGlobalObject } from 'shared/apiUrl';
import { createDocumentationFileSignedRequestUrl } from 'services/s3service';
import { buildSharedReportDownloadUrl } from 'shared/download/sharedResourceDownloadUrls';
import { logWithRethrow } from 'serviceWorker/db/helpers';
import { LogLevel } from 'shared/types/logger';
import { OK } from 'shared/contants';
import type { LogoutAbortRegistry } from 'serviceWorker/helpers/abortableServiceHandler';

/** The server's answer to a public link: every status is an answer the page shows. */
export interface SharedResourceResponse {
  statusCode: number;
  body: unknown;
}

/**
 * Headers of a signed request: the session token whenever the worker holds
 * one (a public link carries its own token in the URL and goes without it).
 */
export function getHeadersForPullSignedRequest(
  setup: config.ConfigData | undefined
): HeadersInit {
  return buildWorkerRequestHeaders(setup?.locale);
}

export async function pullSignedRequest(
  setup: ConfigData | undefined,
  abortController: AbortController,
  url: string,
  options?: PullOptions
): Promise<Response> {
  // Note: Do NOT call ensureAuthenticatedConfig() here. This function is used for both:
  // - Public requests (reports, shared docs) - access token is in URL query param
  // - Authenticated requests (documentation) - caller already called ensureAuthenticatedConfig()
  // getHeadersForPullSignedRequest() handles both cases (with or without auth token).
  // Public callers should pass { authorizedBySession: false }.
  const headers: HeadersInit = getHeadersForPullSignedRequest(setup);

  return pull(
    url,
    {
      ...defaultConfig,
      method: 'GET',
      headers,
      signal: abortController.signal,
    },
    options
  );
}

/**
 * Pulls the signed URL of a public link (report or shared document). The URL
 * carries its own access token, so an unauthorized answer does not log out.
 */
export async function pullSharedResourceResponse(
  setup: ConfigData | undefined,
  abortController: AbortController,
  url: string
): Promise<SharedResourceResponse> {
  const response = await pullSignedRequest(setup, abortController, url, {
    authorizedBySession: false,
  });
  if (response.status !== OK) {
    // A refusal may come without a JSON body (proxy page, empty answer); its status is the answer.
    const refusal: unknown = await response.json().catch(() => undefined);
    return { statusCode: response.status, body: refusal };
  }
  // Not parseResponse: it would not hand over the body as it is.
  const body: unknown = await response.json().catch((e) =>
    logWithRethrow({
      msg: 'Could not parse response',
      logLevel: LogLevel.ERROR,
      errorObj: e,
      additionalInfo: { response },
    })
  );
  return { statusCode: response.status, body };
}

/**
 * Pulls a public link with a controller of the logout registry, registered
 * before any await so a logout meanwhile aborts it too. Reads the stored config
 * only: the URL carries its own access token, so the request must not wait for
 * (or fail on) the user's auth token.
 */
export async function pullPublicLinkResponse(
  abortRegistry: LogoutAbortRegistry | null,
  buildUrl: (setup: ConfigData | undefined) => string
): Promise<SharedResourceResponse> {
  const abortController = abortRegistry?.createController() ?? new AbortController();
  try {
    const setup = await config.get();
    return await pullSharedResourceResponse(setup, abortController, buildUrl(setup));
  } finally {
    abortRegistry?.releaseController(abortController);
  }
}

export function buildSignedRequestUrlForReport(
  setup: config.ConfigData | undefined,
  reportId: string,
  token: string
): string {
  const apiUrl = setup ? setup.api : apiUrlFromGlobalObject;
  return buildSharedReportDownloadUrl({ apiUrl, reportId, token });
}

export function buildSignedRequestUrlForDocumentation(
  setup: config.ConfigData | undefined,
  documentationId: string,
  versionId: string
): string {
  const api = setup ? setup.api : apiUrlFromGlobalObject;
  const projectPrefix = setup?.projectId
    ? `/project/${setup.projectId}`
    : '';
  const path = createDocumentationFileSignedRequestUrl({
    documentationId,
    versionId,
  });
  return `${api}/v2${projectPrefix}${path}`;
}
